import { Tabs, Typography, Button, Tooltip } from '@scale/scaleui';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Skeleton } from '@scale/scaleui-radix';
import QuickGrabTasks from './QuickGrabTasks';
import { QuickGrabTasksResponse, QuickGrabTask } from '@scale/shared/quickGrab/types';
import Kbd from 'react-scale/components/design-system/Kbd';
import { handleCopyTaskIds, handleBulkAudit } from './utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface QuickGrabContentProps {
  data: QuickGrabTasksResponse | undefined;
  isLoading: boolean;
  projectId: string;
  enableKeyboardShortcuts?: boolean;
}

export const QuickGrabContent = ({
  data,
  isLoading,
  projectId,
  enableKeyboardShortcuts = false,
}: QuickGrabContentProps) => {
  const reviewLevelTabs = useMemo(
    () =>
      Object.keys(data?.tasksByReviewLevel ?? {})
        .map(reviewLevel => ({
          label: reviewLevel === 'completed' ? 'Completed' : `L${reviewLevel}`,
          id: reviewLevel,
        }))
        .sort((a, b) => {
          if (a.id === 'completed') return 1;
          if (b.id === 'completed') return -1;
          return parseInt(b.id) - parseInt(a.id);
        }),
    [data?.tasksByReviewLevel],
  );

  const [selectedReviewLevel, setSelectedReviewLevel] = useState(reviewLevelTabs[0]?.id ?? '');
  const [isBulkAuditLoading, setIsBulkAuditLoading] = useState(false);

  const taskIds = useMemo(() => {
    return (
      (data?.tasksByReviewLevel as Record<string, QuickGrabTask[]>)?.[selectedReviewLevel]?.map(
        (task: QuickGrabTask) => task?.taskId,
      ) ?? []
    );
  }, [data?.tasksByReviewLevel, selectedReviewLevel]);

  const allTasksAttempted = useMemo(() => {
    return (
      (data?.tasksByReviewLevel as Record<string, QuickGrabTask[]>)?.[selectedReviewLevel]?.every(
        (task: QuickGrabTask) =>
          task?.lastAttemptedAt && task?.lastAttemptedAt !== '' && task?.lastAttemptedAt !== '-',
      ) ?? false
    );
  }, [data?.tasksByReviewLevel, selectedReviewLevel]);

  const onClickCopyTaskIds = useCallback(() => {
    handleCopyTaskIds(taskIds);
  }, [taskIds]);

  const onClickBulkAudit = useCallback(async () => {
    setIsBulkAuditLoading(true);
    await handleBulkAudit(taskIds, projectId, true);
    setIsBulkAuditLoading(false);
  }, [taskIds, projectId]);

  useEffect(() => {
    setSelectedReviewLevel(reviewLevelTabs[0]?.id ?? '');
  }, [reviewLevelTabs]);

  // keyboard shortcut handlers
  useEffect(() => {
    if (!enableKeyboardShortcuts) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      // Tab navigation with Cmd/Ctrl + Arrow keys
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey) {
        const currentIndex = reviewLevelTabs.findIndex(tab => tab.id === selectedReviewLevel);

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : reviewLevelTabs.length - 1;
          setSelectedReviewLevel(reviewLevelTabs[prevIndex].id);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          const nextIndex = currentIndex < reviewLevelTabs.length - 1 ? currentIndex + 1 : 0;
          setSelectedReviewLevel(reviewLevelTabs[nextIndex].id);
        }
      }

      // Copy Task Ids: Cmd/Ctrl + Shift + C
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'c') {
        event.preventDefault();
        onClickCopyTaskIds();
      }

      // Bulk Audit: Cmd/Ctrl + Shift + B
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'b') {
        event.preventDefault();
        onClickBulkAudit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedReviewLevel,
    reviewLevelTabs,
    onClickCopyTaskIds,
    onClickBulkAudit,
    enableKeyboardShortcuts,
  ]);

  return (
    <>
      <Tabs
        className="mb-4"
        value={selectedReviewLevel}
        tabs={reviewLevelTabs}
        onChange={(reviewLevel: string) => setSelectedReviewLevel(reviewLevel)}
        variant="default"
      />
      <div>
        <div className="flex gap-2 mt-4 mb-4 justify-end">
          <Button
            variant="secondary"
            onClick={onClickCopyTaskIds}
            disabled={!taskIds || !taskIds.length}
          >
            Copy Task Ids
            {enableKeyboardShortcuts && <span className="opacity-60 ml-2">⌘⇧C</span>}
          </Button>
          <Tooltip
            text={
              !allTasksAttempted
                ? 'Bulk Audit is only available when all the listed tasks have an attempt'
                : 'Create a Chat Bulk Audit with the listed tasks for this review level'
            }
            target={
              <Button
                variant="secondary"
                onClick={onClickBulkAudit}
                disabled={
                  !taskIds ||
                  !taskIds.length ||
                  selectedReviewLevel === '' ||
                  !allTasksAttempted ||
                  isBulkAuditLoading
                }
              >
                {isBulkAuditLoading ? (
                  <>
                    Generating Audit Batch...
                    <FontAwesomeIcon
                      icon={['fas', 'spinner']}
                      spin={isBulkAuditLoading}
                      className="ml-1"
                    />
                  </>
                ) : (
                  'Bulk Audit'
                )}
                {enableKeyboardShortcuts && <span className="opacity-60 ml-2">⌘⇧B</span>}
              </Button>
            }
          />
        </div>
      </div>
      {!data || isLoading ? (
        <Skeleton className="h-[200px] w-full" />
      ) : (
        <div className="mt-4">
          <QuickGrabTasks
            quickGrabTasks={
              (data?.tasksByReviewLevel as Record<string, QuickGrabTask[]>)?.[
                selectedReviewLevel
              ] ?? []
            }
          />
          {enableKeyboardShortcuts && (
            <div className="flex flex-shrink-0 gap-1 items-center px-3 py-2 bg-neutral-50 border-t border-t-neutral-200 shadow mt-4">
              <div className="flex items-center w-full">
                <Typography.Overline3>{'Navigate Review Levels with'}</Typography.Overline3>
                <Kbd>⌘</Kbd>
                <Typography.Overline3>{' +'}</Typography.Overline3>
                <Kbd>ArrowKeys</Kbd>
              </div>
              <div className="flex items-center justify-end w-full">
                <Typography.Overline3>{'Press'}</Typography.Overline3>
                <Kbd>Escape</Kbd>
                <Typography.Overline3>{'to close.'}</Typography.Overline3>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
